import { Controller } from 'react-hook-form';
import { UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AGE_OPTIONS, GENDER_OPTIONS } from '@/features/audiences/model';
import { normalizeDashes } from '@/features/audiences/lib';
import { FormSectionV2, PersonaSelectField, PersonaTextField } from '../../PersonaFormSectionsV2';
import { isFieldRequired } from '../EditPersonaForm.schema';
import { useEditPersonaFormContext } from '../hooks/useEditPersonaFormContext';

/* eslint-disable i18next/no-literal-string -- react-hook-form field paths, not copy */
/** Free-text demographic fields, in the order the V1 grid lays them out. */
const TEXT_FIELDS = [
  { name: 'location', labelKey: 'audiences.demographicsSection.location' },
  { name: 'education', labelKey: 'audiences.demographicsSection.education' },
  { name: 'familyComposition', labelKey: 'audiences.demographicsSection.familyComposition' },
  { name: 'lifeStage', labelKey: 'audiences.demographicsSection.lifeStage' },
] as const;
/* eslint-enable i18next/no-literal-string */

export function DemographicsSectionV2() {
  const { t } = useTranslation('audiences');
  const { control } = useEditPersonaFormContext();

  return (
    <FormSectionV2
      icon={<UsersIcon className="size-5" />}
      title={t('audiences.demographicsSection.demographics')}
    >
      <div className="grid grid-cols-2 gap-sm">
        <Controller
          name="age"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaSelectField
              {...field}
              label={t('audiences.demographicsSection.age')}
              placeholder={t('audiences.demographicsSection.selectAge')}
              required={isFieldRequired(field.name)}
              error={error?.message}
              options={AGE_OPTIONS}
            />
          )}
        />
        <Controller
          name="gender"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaSelectField
              {...field}
              label={t('audiences.demographicsSection.gender')}
              placeholder={t('audiences.demographicsSection.selectGender')}
              required={isFieldRequired(field.name)}
              error={error?.message}
              options={GENDER_OPTIONS}
            />
          )}
        />

        {TEXT_FIELDS.map((textField) => (
          <Controller
            key={textField.name}
            name={textField.name}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                label={t(textField.labelKey)}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
        ))}

        <Controller
          name="annualIncome"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              onChange={(next) => field.onChange(normalizeDashes(next))}
              label={t('audiences.demographicsSection.annualIncome')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
      </div>
    </FormSectionV2>
  );
}

DemographicsSectionV2.displayName = 'DemographicsSectionV2';
